import { type ReactNode, type Ref, useRef } from 'react';
import type { DefaultValues, FieldValues, Mode } from 'react-hook-form';

import { ignorePromiseRejection } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

import FormProvider from './FormProvider';
import { type FormErrors, setFormErrors } from './helpers';
import { useForm } from './hooks';

interface Props<T extends FieldValues> {
	children: ReactNode;
	className?: string;
	/** Root error message when the submit fails, defaults to a generic text. */
	criticalErrorMessage?: string;
	defaultValues?: DefaultValues<T>;
	/** Form name sent with GTM validation error events. */
	formLocation: string;
	id?: string;
	mode?: Mode;
	/** Submit handler, returns errors to set or nothing on success. */
	onSubmit: (values: T) => FormErrors | void | Promise<FormErrors | void>;
	onSuccess?: (values: T) => void;
	ref?: Ref<HTMLFormElement>;
	/** Reset to the default values after a successful submit. */
	resetOnSuccess?: boolean;
}

/**
 * Form with React Hook Form setup and submit handling.
 *
 * Sets the errors returned from `onSubmit`, or a `root.criticalError` when
 * `onSubmit` throws or rejects.
 */
export default function Form<T extends FieldValues>({
	children,
	className,
	criticalErrorMessage,
	defaultValues,
	formLocation,
	id,
	mode = 'onSubmit',
	onSubmit,
	onSuccess,
	ref,
	resetOnSuccess = false,
}: Props<T>) {
	const { t } = useI18n();
	const form = useForm<T>({ defaultValues, mode });
	// Checked before handleSubmit, since an ignored submit that runs
	// handleSubmit sets isSubmitting to false while the first is pending.
	const isSubmittingRef = useRef(false);

	const handleValid = async (values: T) => {
		let errors: FormErrors | void;
		try {
			errors = await onSubmit(values);
		} catch {
			form.setError('root.criticalError', {
				message: criticalErrorMessage || t('general_critical_error_text'),
			});
			return;
		}
		if (errors) {
			setFormErrors({ errors, setError: form.setError, values });
			return;
		}
		onSuccess?.(values);
		if (resetOnSuccess) {
			form.reset();
		}
	};

	return (
		<FormProvider form={form} formLocation={formLocation}>
			<form
				ref={ref}
				className={className}
				id={id}
				onSubmit={(event) => {
					event.preventDefault();
					if (isSubmittingRef.current) {
						return;
					}
					isSubmittingRef.current = true;
					ignorePromiseRejection(
						form
							.handleSubmit(handleValid)(event)
							.finally(() => {
								isSubmittingRef.current = false;
							}),
					);
				}}
			>
				{children}
			</form>
		</FormProvider>
	);
}
Form.displayName = 'ReactHookForm_Form';
